import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  Container, 
  Grid, 
  Card, 
  CardContent, 
  CardActions,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SecurityIcon from '@mui/icons-material/Security';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import EngineeringIcon from '@mui/icons-material/Engineering';
import Image from '../components/Image';

// Import images from centralized image config
import { HOME, SERVICES } from '../assets/images/imageConfig';

// Images from the centralized config
const HERO_BANNER = HOME.HERO;
const SECURITY_IMAGE = SERVICES.SECURITY;
const CLEANING_IMAGE = SERVICES.HOUSEKEEPING;
const LABOR_IMAGE = SERVICES.SKILLED_LABOR;
const CTA_BACKGROUND = HOME.TESTIMONIAL_BG;

const Home = () => {
  return (
    <Box>
      {/* Hero Section */}
      <Box 
        sx={{
          position: 'relative',
          height: { xs: '60vh', md: '75vh' },
          display: 'flex',
          alignItems: 'center',
          color: 'white',
          overflow: 'hidden'
        }}
      >
        <Box 
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: -1,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              zIndex: 1
            }
          }}
        >
          <Image 
            src={HERO_BANNER}
            alt="Security Services"
            sx={{ width: '100%', height: '100%' }}
          />
        </Box>
        <Container maxWidth="lg">
          <Box 
            sx={{ 
              textAlign: 'center', 
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              py: 5,
              px: { xs: 3, md: 6 },
              borderRadius: 2,
              maxWidth: 900,
              mx: 'auto'
            }}
          >
            <Typography 
              variant="h2" 
              component="h1" 
              gutterBottom
              sx={{ 
                fontWeight: 700,
                fontSize: { xs: '2rem', sm: '3rem', md: '3.5rem' }
              }}
            >
              SafeNext - Your Trusted Manpower Partner
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                mb: 4,
                fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }
              }}
            >
              Providing quality security, housekeeping, and manpower services for businesses and individuals
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button 
                variant="contained" 
                size="large" 
                component={Link} 
                to="/contact"
                sx={{ 
                  py: 1.5, 
                  px: 4,
                  fontSize: '1.1rem'
                }}
              >
                Hire Now
              </Button>
              <Button 
                variant="outlined" 
                size="large" 
                component={Link} 
                to="/services"
                sx={{ 
                  py: 1.5, 
                  px: 4,
                  fontSize: '1.1rem',
                  color: 'white',
                  borderColor: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                Get Services
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Company Introduction */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" component="h2" gutterBottom color="primary" sx={{ fontWeight: 700 }}>
                Welcome to SafeNext
              </Typography>
              <Typography variant="h5" sx={{ mb: 2, color: 'text.secondary' }}>
                Your Reliable Partner for Security and Manpower Solutions
              </Typography>
              <Typography paragraph>
                At SafeNext, we understand that the safety and cleanliness of your premises, as well as having 
                reliable manpower, are crucial for your business operations. With over a decade of experience 
                in the industry, we have established ourselves as a leading provider of comprehensive 
                security, housekeeping, and manpower solutions.
              </Typography>
              <Typography paragraph>
                Our team of well-trained professionals ensures that your needs are met with the highest 
                standards of service delivery, giving you peace of mind and allowing you to focus on your 
                core business activities.
              </Typography>
              <Button 
                variant="contained" 
                component={Link} 
                to="/about" 
                sx={{ mt: 2 }}
              >
                Learn More About Us
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h5" gutterBottom color="primary" sx={{ fontWeight: 700 }}>
                  Why Choose SafeNext?
                </Typography>
                <List>
                  {[
                    'Professionally trained staff',
                    '24/7 service availability',
                    'Customized solutions',
                    'Competitive pricing',
                    'Reliable and trustworthy',
                    'Quick response time'
                  ].map((item, index) => (
                    <ListItem key={index} sx={{ p: 1 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Services Overview */}
      <Box sx={{ py: 8, backgroundColor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" align="center" gutterBottom color="primary" sx={{ fontWeight: 700 }}>
            Our Services
          </Typography>
          <Box sx={{ mb: 6 }}></Box>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', transition: 'transform 0.3s ease', '&:hover': { transform: 'translateY(-8px)' } }}>
                <Box sx={{ height: 240, overflow: 'hidden' }}>
                  <Image 
                    src={SECURITY_IMAGE}
                    alt="Security Guard Services"
                    sx={{ height: 240 }}
                  />
                </Box>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SecurityIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h5" component="h3" color="primary" sx={{ fontWeight: 600 }}>
                      Security Guard Services
                    </Typography>
                  </Box>
                  <Typography>
                    Our trained security guards provide reliable protection for your premises, 
                    assets, and people. We offer both armed and unarmed security personnel.
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button component={Link} to="/services" variant="contained">
                    Learn More
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', transition: 'transform 0.3s ease', '&:hover': { transform: 'translateY(-8px)' } }}>
                <Box sx={{ height: 240, overflow: 'hidden' }}>
                  <Image 
                    src={CLEANING_IMAGE}
                    alt="Housekeeping / Cleaning Services"
                    sx={{ height: 240 }}
                  />
                </Box>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CleaningServicesIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h5" component="h3" color="primary" sx={{ fontWeight: 600 }}>
                      Housekeeping / Cleaning
                    </Typography>
                  </Box>
                  <Typography>
                    Maintain a clean and hygienic environment with our professional housekeeping services. 
                    We serve residential, commercial, and industrial clients.
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button component={Link} to="/services" variant="contained">
                    Learn More
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', transition: 'transform 0.3s ease', '&:hover': { transform: 'translateY(-8px)' } }}>
                <Box sx={{ height: 240, overflow: 'hidden' }}>
                  <Image 
                    src={LABOR_IMAGE}
                    alt="Skilled/Unskilled Labor Services"
                    sx={{ height: 240 }}
                  />
                </Box>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <EngineeringIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h5" component="h3" color="primary" sx={{ fontWeight: 600 }}>
                      Skilled/Unskilled Labor
                    </Typography>
                  </Box>
                  <Typography>
                    We provide skilled and unskilled labor for various industries. 
                    Our workers are reliable, efficient, and trained for their specific roles.
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button component={Link} to="/services" variant="contained">
                    Learn More
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box 
        sx={{
          position: 'relative',
          py: 8,
          color: 'white',
          overflow: 'hidden'
        }}
      >
        <Box 
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: -1,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 22, 64, 0.8)',
              zIndex: 1
            }
          }}
        >
          <Image 
            src={CTA_BACKGROUND}
            alt="Call to action background"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </Box>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', position: 'relative' }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
              Ready to Get Started?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
              Contact us today to discuss how our services can benefit your business or home.
              We offer customized solutions to fit your specific needs.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button 
                variant="contained" 
                size="large" 
                component={Link} 
                to="/contact"
                sx={{ 
                  py: 1.5, 
                  px: 4, 
                  fontSize: '1.1rem',
                  backgroundColor: 'white',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              >
                Contact Us Now
              </Button>
              <Button 
                variant="outlined" 
                size="large" 
                component={Link} 
                to="/services"
                sx={{ 
                  py: 1.5, 
                  px: 4,
                  fontSize: '1.1rem',
                  color: 'white',
                  borderColor: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                View Our Services
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Home; 